import { Fragment, useState } from "react";

const T = {
  bg: "#000000",
  surface: "#0A0A0A",
  surfaceRaised: "#111111",
  surfaceHover: "#1A1A1A",
  border: "#1C1C1E",
  text: "#FFFFFF",
  textSecondary: "#8E8E93",
  textTertiary: "#48484A",
  accent: "#007AFF",
  accentDim: "rgba(0,122,255,0.12)",
  green: "#30D158",
  greenDim: "rgba(48,209,88,0.12)",
  red: "#FF453A",
  redDim: "rgba(255,69,58,0.12)",
  orange: "#FF9F0A",
  orangeDim: "rgba(255,159,10,0.12)",
  purple: "#BF5AF2",
  purpleDim: "rgba(191,90,242,0.12)",
  cyan: "#64D2FF",
  cyanDim: "rgba(100,210,255,0.12)",
};

const SAMPLE_PROJECT = {
  id: 1,
  title: "Spatiotemporal PM2.5 Prediction",
  subtitle: "Predicting air quality at stations without PM2.5 monitors using nearby stations' data",
  dateRange: "Jan 2017 - Jan 2022",
  targetStation: "Mira Loma, CA",
  nearbyStations: ["Upland", "Lake Elsinore", "Temecula", "Banning", "Central San Bernardino"],
  tags: ["Python", "TensorFlow", "Keras", "XGBoost", "scikit-learn", "PCA"],
  models: [
    {
      name: "CNN",
      architecture: [
        { label: "Input", detail: "168 x 11", color: T.textSecondary },
        { label: "Conv1D", detail: "12 filters, k=24", color: T.accent },
        { label: "BatchNorm", detail: "", color: T.accent },
        { label: "ReLU", detail: "", color: T.accent },
        { label: "AvgPool1D", detail: "pool=2", color: T.accent },
        { label: "Flatten", detail: "", color: T.textSecondary },
        { label: "Dense", detail: "16 units", color: T.purple },
        { label: "Dense", detail: "8 units", color: T.purple },
        { label: "Output", detail: "1 (PM2.5)", color: T.green },
      ],
      hyperparameters: {
        optimizer: "Adam",
        learning_rate: "0.001",
        loss: "MSE",
        epochs: "5",
        batch_size: "256",
        callbacks: "ReduceLR, EarlyStopping",
      },
      trainingHistory: {
        epochs: [1, 2, 3, 4, 5],
        trainLoss: [97.28, 58.69, 53.13, 50.27, 48.57],
        valLoss: [101.10, 91.21, 97.52, 78.69, 80.91],
        trainMAE: [6.88, 5.21, 4.92, 4.76, 4.70],
        valMAE: [6.95, 6.57, 6.79, 6.10, 6.09],
      },
      metrics: {
        main: { R2: 0.387, MSE: 70.67, MAE: 5.54, P: 0.723 },
        base: { R2: 0.467, MSE: 61.46, MAE: 5.33, P: 0.732 },
      },
    },
    {
      name: "LSTM",
      architecture: [
        { label: "Input", detail: "168 x 11", color: T.textSecondary },
        { label: "LSTM", detail: "64 units", color: T.orange },
        { label: "Dense", detail: "16 units, ReLU", color: T.purple },
        { label: "Dense", detail: "8 units, ReLU", color: T.purple },
        { label: "Output", detail: "1 (PM2.5)", color: T.green },
      ],
      hyperparameters: {
        optimizer: "Adam",
        learning_rate: "0.001",
        loss: "MSE",
        epochs: "5",
        batch_size: "256",
        callbacks: "ReduceLR, EarlyStopping",
      },
      trainingHistory: {
        epochs: [1, 2, 3, 4, 5],
        trainLoss: [166.94, 67.98, 59.37, 54.84, 51.59],
        valLoss: [110.60, 97.77, 90.24, 83.15, 84.62],
      },
      metrics: {
        main: { R2: 0.345, MSE: 75.52, MAE: 5.63, P: 0.701 },
        base: { R2: 0.432, MSE: 65.43, MAE: 5.32, P: 0.733 },
      },
    },
    {
      name: "XGBoost",
      architecture: [
        { label: "Input", detail: "168 x 11", color: T.textSecondary },
        { label: "Unroll", detail: "1,848 features", color: T.cyan },
        { label: "PCA", detail: "150 components", color: T.cyan },
        { label: "XGBRegressor", detail: "default params", color: T.orange },
        { label: "Output", detail: "1 (PM2.5)", color: T.green },
      ],
      hyperparameters: {
        algorithm: "XGBRegressor",
        pca_components: "150",
        input_unrolled: "1,848",
        tuning: "Default + grid search",
      },
      trainingHistory: null,
      metrics: {
        main: { R2: 0.281, MSE: 82.91, MAE: 6.09, P: 0.684 },
        base: { R2: 0.353, MSE: 74.58, MAE: 5.89, P: 0.631 },
      },
    },
  ],
  features: [
    { name: "CO", source: "Mira Loma" },
    { name: "O3", source: "Mira Loma" },
    { name: "NO2", source: "Mira Loma" },
    { name: "Wind Speed", source: "Mira Loma" },
    { name: "Wind Dir.", source: "Mira Loma" },
    { name: "Temperature", source: "Mira Loma" },
    { name: "PM2.5 x 5", source: "Nearby Stations" },
  ],
  notebookUrl: "#",
  reportUrl: "#",
  datasetUrl: "#",
};

const FEATURE_IMPORTANCE = [
  { name: "Upland PM2.5", value: 0.92, color: T.accent },
  { name: "C.S.B. PM2.5", value: 0.85, color: T.accent },
  { name: "Banning PM2.5", value: 0.78, color: T.accent },
  { name: "Lake Elsinore PM2.5", value: 0.71, color: T.accent },
  { name: "Temecula PM2.5", value: 0.65, color: T.accent },
  { name: "NO2", value: 0.42, color: T.orange },
  { name: "CO", value: 0.38, color: T.orange },
  { name: "Temperature", value: 0.31, color: T.orange },
  { name: "O3", value: 0.27, color: T.orange },
  { name: "Wind Speed", value: 0.19, color: T.cyan },
  { name: "Wind Direction", value: 0.14, color: T.cyan },
];

const mono = "'SF Mono','Fira Code','Cascadia Code','Consolas',monospace";
const sans = "-apple-system,BlinkMacSystemFont,'SF Pro Display','Segoe UI',system-ui,sans-serif";

const css = `
@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
@keyframes growBar{from{width:0}}
@keyframes scaleIn{from{opacity:0;transform:scale(.86)}to{opacity:1;transform:scale(1)}}
@keyframes dashDraw{from{stroke-dashoffset:600}to{stroke-dashoffset:0}}
*{box-sizing:border-box}
::-webkit-scrollbar{width:4px;height:4px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:${T.border};border-radius:4px}
.tab-btn:hover,.model-btn:hover{background:${T.surfaceHover}!important}
.pipeline-card{transition:border-color .2s,transform .2s,box-shadow .2s}
.pipeline-card:hover{border-color:${T.accent}55!important;transform:translateY(-3px);box-shadow:0 8px 24px rgba(0,122,255,.08)}
.link-card{transition:border-color .2s,transform .15s,box-shadow .2s}
.link-card:hover{transform:translateY(-2px);box-shadow:0 6px 20px rgba(0,0,0,.3)}
.bar-row{transition:transform .15s}
.bar-row:hover{transform:translateX(2px)}
.bar-row:hover .bar-fill{filter:brightness(1.3)}
`;

const SectionLabel = ({ children, delay = 0 }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: ".12em",
      textTransform: "uppercase",
      color: T.textSecondary,
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      gap: 8,
      animation: `fadeUp .4s ease ${delay}s both`,
    }}
  >
    <span style={{ width: 3, height: 12, borderRadius: 2, background: T.accent, display: "inline-block" }} />
    {children}
  </div>
);

const Tag = ({ children, color = T.accent, bg = T.accentDim }) => (
  <span
    style={{
      display: "inline-flex",
      padding: "3px 10px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      fontFamily: mono,
      color,
      background: bg,
      border: `1px solid ${color}22`,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

function MetricCard({ label, value, unit, color = T.accent, subtitle, delay = 0 }) {
  const strVal = String(value);
  const isLong = strVal.length > 6;
  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: isLong ? 120 : 98,
        padding: "16px 12px",
        borderRadius: 12,
        background: T.surfaceRaised,
        border: `1px solid ${T.border}`,
        textAlign: "center",
        animation: `fadeUp .45s ease ${delay}s both`,
      }}
    >
      <div style={{ fontSize: 9, color: T.textSecondary, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: isLong ? 14 : 23, fontWeight: isLong ? 700 : 800, color, fontFamily: isLong ? sans : mono, lineHeight: 1.2, wordBreak: "break-word" }}>
        {value}
        {unit && <span style={{ fontSize: 12, color: T.textSecondary, marginLeft: 2 }}>{unit}</span>}
      </div>
      {subtitle && <div style={{ fontSize: 10, color: T.textTertiary, marginTop: 5 }}>{subtitle}</div>}
    </div>
  );
}

function TabBar({ active, onChange }) {
  const tabs = ["Overview", "Training", "Evaluation", "Reproduce"];
  return (
    <div style={{ display: "flex", gap: 2, padding: 3, borderRadius: 10, background: T.surfaceRaised, border: `1px solid ${T.border}`, width: "fit-content", maxWidth: "100%", overflowX: "auto" }}>
      {tabs.map((tab) => (
        <button
          key={tab}
          className="tab-btn"
          onClick={() => onChange(tab)}
          style={{
            padding: "7px 16px",
            borderRadius: 8,
            border: "none",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            background: active === tab ? T.accent : "transparent",
            color: active === tab ? "#fff" : T.textSecondary,
            fontFamily: sans,
            transition: "all .2s",
            whiteSpace: "nowrap",
          }}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}

function ModelSwitcher({ models, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", animation: "fadeUp .35s ease both" }}>
      {models.map((model, i) => (
        <button
          key={model.name}
          className="model-btn"
          onClick={() => onChange(i)}
          style={{
            border: `1px solid ${active === i ? T.accent : T.border}`,
            background: active === i ? T.accentDim : T.surfaceRaised,
            color: active === i ? T.accent : T.textSecondary,
            borderRadius: 10,
            padding: "8px 12px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: sans,
            transition: "all .2s",
          }}
        >
          {model.name}
          <span style={{ color: T.textTertiary, marginLeft: 6, fontSize: 10 }}>
            R2 {model.metrics.main.R2.toFixed(2)}
          </span>
        </button>
      ))}
    </div>
  );
}

function ArchitecturePipeline({ layers }) {
  return (
    <div style={{ overflowX: "auto", paddingBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", minWidth: "max-content" }}>
        {layers.map((layer, i) => (
          <Fragment key={`${layer.label}-${i}`}>
            <div
              className="pipeline-card"
              style={{
                width: 110,
                minHeight: 78,
                padding: "12px 8px 10px",
                borderRadius: 12,
                background: T.surfaceRaised,
                border: `1px solid ${layer.color}33`,
                textAlign: "center",
                animation: `scaleIn .35s ease ${i * 0.06}s both`,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 800, color: layer.color, lineHeight: 1.2, marginBottom: 5 }}>{layer.label}</div>
              {layer.detail && <div style={{ fontSize: 9, color: T.textSecondary, lineHeight: 1.35, fontFamily: mono }}>{layer.detail}</div>}
            </div>
            {i < layers.length - 1 && (
              <svg width="24" height="12" viewBox="0 0 24 12" style={{ flexShrink: 0, animation: `fadeIn .3s ease ${i * 0.06 + 0.1}s both` }}>
                <line x1="0" y1="6" x2="16" y2="6" stroke={T.textTertiary} strokeWidth="1.2" />
                <path d="M14 2.5l5.5 3.5-5.5 3.5" fill="none" stroke={T.accent} strokeWidth="1.3" strokeOpacity=".6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function HyperparameterChips({ params }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {Object.entries(params).map(([key, value], i) => (
        <div key={key} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", borderRadius: 8, background: T.surfaceRaised, border: `1px solid ${T.border}`, fontSize: 11, fontFamily: mono, animation: `fadeIn .3s ease ${i * 0.03}s both` }}>
          <span style={{ color: T.textSecondary }}>{key}</span>
          <span style={{ color: T.textTertiary }}>:</span>
          <span style={{ color: T.orange, fontWeight: 600 }}>{String(value)}</span>
        </div>
      ))}
    </div>
  );
}

function LossChart({ history }) {
  const [hover, setHover] = useState(null);
  if (!history) {
    return (
      <div style={{ height: 230, color: T.textTertiary, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", background: T.surfaceRaised, borderRadius: 14, border: `1px solid ${T.border}` }}>
        No epoch-level training data (tree-based model)
      </div>
    );
  }

  const data = history.epochs.map((epoch, i) => ({ epoch, train: history.trainLoss[i], val: history.valLoss?.[i] }));
  const values = data.flatMap((d) => [d.train, d.val].filter((v) => typeof v === "number"));
  const max = Math.max(...values);
  const min = Math.min(...values) * 0.85;
  const range = max - min || 1;
  const W = 540;
  const H = 230;
  const pad = { t: 24, r: 20, b: 34, l: 46 };
  const w = W - pad.l - pad.r;
  const h = H - pad.t - pad.b;
  const x = (i) => pad.l + (i / Math.max(1, data.length - 1)) * w;
  const y = (value) => pad.t + (1 - (value - min) / range) * h;
  const path = (key) => data.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(" ");

  return (
    <div style={{ background: T.surfaceRaised, borderRadius: 14, border: `1px solid ${T.border}`, padding: "14px 10px 6px", animation: "fadeUp .4s ease both" }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        <defs>
          <linearGradient id="pm25LossFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.accent} stopOpacity=".18" />
            <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
          const gy = pad.t + frac * h;
          const gv = max - frac * range;
          return (
            <g key={frac}>
              <line x1={pad.l} y1={gy} x2={W - pad.r} y2={gy} stroke={T.border} strokeWidth=".5" />
              <text x={pad.l - 6} y={gy + 3} textAnchor="end" fill={T.textTertiary} fontSize="8.5" fontFamily={mono}>
                {gv.toFixed(0)}
              </text>
            </g>
          );
        })}
        <path d={`${path("train")} L${x(data.length - 1)},${y(min)} L${x(0)},${y(min)} Z`} fill="url(#pm25LossFill)" />
        <path d={path("train")} fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ strokeDasharray: 600, animation: "dashDraw 1.2s ease both" }} />
        {history.valLoss && <path d={path("val")} fill="none" stroke={T.orange} strokeWidth="2" strokeDasharray="5 3" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "fadeIn .8s ease .4s both" }} />}
        {data.map((d, i) => (
          <g key={d.epoch} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: "crosshair" }}>
            <rect x={x(i) - 16} y={pad.t - 5} width="32" height={h + 10} fill="transparent" />
            {hover === i && (
              <>
                <line x1={x(i)} y1={pad.t} x2={x(i)} y2={pad.t + h} stroke={T.textTertiary} strokeWidth=".5" strokeDasharray="3 2" />
                <circle cx={x(i)} cy={y(d.train)} r="4.5" fill={T.accent} stroke={T.bg} strokeWidth="2" />
                {typeof d.val === "number" && <circle cx={x(i)} cy={y(d.val)} r="4.5" fill={T.orange} stroke={T.bg} strokeWidth="2" />}
                <rect x={Math.max(8, Math.min(W - 138, x(i) - 65))} y={Math.max(8, y(d.train) - 44)} width="130" height="34" rx="7" fill={T.surfaceRaised} stroke={T.border} />
                <text x={Math.max(73, Math.min(W - 73, x(i)))} y={Math.max(28, y(d.train) - 23)} textAnchor="middle" fill={T.text} fontSize="9.5" fontFamily={mono} fontWeight="600">
                  {`E${d.epoch} T:${d.train.toFixed(1)}${typeof d.val === "number" ? ` V:${d.val.toFixed(1)}` : ""}`}
                </text>
              </>
            )}
          </g>
        ))}
        <g transform={`translate(${pad.l + 6},${pad.t + 2})`}>
          <line x1="0" y1="0" x2="14" y2="0" stroke={T.accent} strokeWidth="2" />
          <text x="18" y="3.5" fill={T.textSecondary} fontSize="9" fontFamily={mono}>Train</text>
          {history.valLoss && (
            <>
              <line x1="56" y1="0" x2="70" y2="0" stroke={T.orange} strokeWidth="2" strokeDasharray="4 3" />
              <text x="74" y="3.5" fill={T.textSecondary} fontSize="9" fontFamily={mono}>Val</text>
            </>
          )}
        </g>
      </svg>
    </div>
  );
}

function MetricsComparison({ models }) {
  const keys = ["MSE", "MAE", "R2", "P"];
  const labels = { MSE: "MSE ↓ lower is better", MAE: "MAE ↓ lower is better", R2: "R² ↑ higher is better", P: "Accuracy P ↑ higher is better" };
  const colors = [T.accent, T.orange, T.cyan];
  const fmtVal = (key, value) => {
    if (key === "MSE") return value.toFixed(1);
    if (key === "MAE") return value.toFixed(2);
    if (key === "P") return (value * 100).toFixed(1) + "%";
    return value.toFixed(3);
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 12 }}>
      {keys.map((key, ki) => {
        const values = models.flatMap((model) => [model.metrics.main[key], model.metrics.base[key]]);
        const max = Math.max(...values) * 1.15;
        return (
          <div key={key} style={{ background: T.surfaceRaised, borderRadius: 12, border: `1px solid ${T.border}`, padding: 14, animation: `fadeUp .35s ease ${ki * 0.05}s both` }}>
            <div style={{ fontSize: 10, color: T.textSecondary, marginBottom: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>
              {labels[key]}
            </div>
            {models.map((model, index) => (
              <div key={model.name} style={{ marginBottom: index < models.length - 1 ? 10 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 2, background: colors[index], display: "inline-block", flexShrink: 0 }} />
                  <span style={{ fontSize: 10, color: T.textTertiary }}>{model.name}</span>
                </div>
                {["main", "base"].map((kind) => {
                  const value = model.metrics[kind][key];
                  return (
                    <div key={kind} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      <div style={{ width: 30, fontSize: 8, color: kind === "main" ? T.textSecondary : T.textTertiary, fontFamily: mono, flexShrink: 0 }}>
                        {kind === "main" ? "Main" : "Base"}
                      </div>
                      <div style={{ flex: 1, height: 12, background: `${T.border}66`, borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${Math.max((value / max) * 100, 1)}%`, height: "100%", borderRadius: 3, background: kind === "main" ? colors[index] : `${colors[index]}66`, animation: `growBar .55s ease ${(ki * 3 + index) * 0.04}s both` }} />
                      </div>
                      <span style={{ fontSize: 9, color: kind === "main" ? T.text : T.textSecondary, fontFamily: mono, minWidth: 44, textAlign: "right", flexShrink: 0 }}>
                        {fmtVal(key, value)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function FeatureImportance() {
  const [hoverIdx, setHoverIdx] = useState(null);
  const max = FEATURE_IMPORTANCE[0]?.value || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {FEATURE_IMPORTANCE.map((feature, i) => {
        const pct = (feature.value / max) * 100;
        const hov = hoverIdx === i;
        return (
          <div
            key={feature.name}
            className="bar-row"
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(null)}
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "default", animation: `slideIn .3s ease ${i * 0.03}s both` }}
          >
            <div style={{ width: 120, fontSize: 10, fontFamily: mono, color: hov ? T.text : T.textSecondary, textAlign: "right", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", transition: "color .15s" }}>
              {feature.name}
            </div>
            <div style={{ flex: 1, height: 16, background: T.surfaceRaised, borderRadius: 4, overflow: "hidden", position: "relative" }}>
              <div style={{ height: "100%", width: `${pct}%`, borderRadius: 4, background: hov ? `linear-gradient(90deg,${feature.color},${T.cyan})` : `linear-gradient(90deg,${feature.color}cc,${feature.color}44)`, transition: "background .2s", animation: `growBar .5s ease ${i * 0.04 + 0.15}s both` }} />
            </div>
            <div style={{ fontSize: 10, color: hov ? T.text : T.textTertiary, fontFamily: mono, width: 34, textAlign: "right", transition: "color .15s", fontWeight: hov ? 700 : 400 }}>
              {(feature.value * 100).toFixed(0)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReproduceIt({ notebookUrl, reportUrl, datasetUrl }) {
  const links = [
    { label: "Open Notebook", url: notebookUrl, color: T.orange, desc: "Colab / .ipynb" },
    { label: "Download Report", url: reportUrl, color: T.accent, desc: "PDF - 8 pages" },
    { label: "Get Dataset", url: datasetUrl, color: T.green, desc: "6 CSVs - EPA AQMD" },
  ];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {links.map((link, i) => (
        <a
          key={link.label}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="link-card"
          style={{
            display: "inline-flex",
            flexDirection: "column",
            gap: 4,
            minWidth: 150,
            padding: "12px 16px",
            borderRadius: 10,
            background: T.surfaceRaised,
            border: `1px solid ${T.border}`,
            color: link.color,
            fontSize: 12,
            fontWeight: 700,
            textDecoration: "none",
            cursor: "pointer",
            fontFamily: sans,
            animation: `fadeUp .35s ease ${i * 0.08}s both`,
          }}
        >
          <span>{link.label}</span>
          <span style={{ color: T.textTertiary, fontSize: 10, fontWeight: 500 }}>{link.desc}</span>
        </a>
      ))}
    </div>
  );
}

function MethodologyNote() {
  return (
    <div style={{ background: T.accentDim, border: `1px solid ${T.accent}22`, borderRadius: 14, padding: "16px 20px", animation: "fadeUp .4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 6 }}>Methodology Note</div>
      <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6 }}>
        Main models predict PM2.5 at a target station <em>without</em> access to its own PM2.5 history - using only nearby
        stations' PM2.5 and local meteorological data (CO, O3, NO2, wind, temperature). Base models include the target's
        own PM2.5 as an additional input for comparison. A 168-hour sliding window feeds 6-hour-ahead predictions. Data is
        standardized per-feature using training set statistics.
      </div>
    </div>
  );
}

export default function MLModelView({ project = SAMPLE_PROJECT }) {
  const [tab, setTab] = useState("Overview");
  const [activeModel, setActiveModel] = useState(0);
  const model = project.models[activeModel];

  return (
    <div style={{ width: "100%", minHeight: "100%", background: T.bg, color: T.text, fontFamily: sans, WebkitFontSmoothing: "antialiased" }}>
      <style>{css}</style>

      <div style={{ maxWidth: 896, margin: "0 auto", padding: "28px 24px 0" }}>
        <div style={{ animation: "fadeUp .4s ease both" }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.025em", margin: "0 0 4px", lineHeight: 1.2 }}>{project.title}</h1>
          <p style={{ fontSize: 13, color: T.textSecondary, margin: "0 0 14px", lineHeight: 1.65, maxWidth: 660 }}>{project.subtitle}</p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14, animation: "fadeUp .4s ease .05s both" }}>
          {project.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
        </div>
        <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap", animation: "fadeUp .4s ease .08s both" }}>
          <span style={{ fontSize: 11, color: T.textTertiary }}><span style={{ color: T.textSecondary }}>Target:</span> {project.targetStation}</span>
          <span style={{ fontSize: 11, color: T.textTertiary }}><span style={{ color: T.textSecondary }}>Period:</span> {project.dateRange}</span>
          <span style={{ fontSize: 11, color: T.textTertiary }}><span style={{ color: T.textSecondary }}>Window:</span> 168h - 6h forecast</span>
        </div>
        <div style={{ animation: "fadeUp .4s ease .1s both" }}>
          <TabBar active={tab} onChange={setTab} />
        </div>
      </div>

      <div key={tab} style={{ maxWidth: 896, margin: "0 auto", padding: 24 }}>
        {tab === "Overview" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
            <div>
              <SectionLabel>Model</SectionLabel>
              <ModelSwitcher models={project.models} active={activeModel} onChange={setActiveModel} />
            </div>
            <div>
              <SectionLabel delay={0.05}>Key Metrics</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <MetricCard label="R2 Score" value={model.metrics.main.R2.toFixed(3)} color={T.accent} subtitle="Main model" delay={0.08} />
                <MetricCard label="MSE" value={model.metrics.main.MSE.toFixed(1)} color={T.orange} subtitle={`Base: ${model.metrics.base.MSE.toFixed(1)}`} delay={0.12} />
                <MetricCard label="MAE" value={model.metrics.main.MAE.toFixed(2)} color={T.purple} subtitle={`Base: ${model.metrics.base.MAE.toFixed(2)}`} delay={0.16} />
                <MetricCard label="Accuracy P" value={(model.metrics.main.P * 100).toFixed(1)} unit="%" color={T.green} subtitle={`Base: ${(model.metrics.base.P * 100).toFixed(1)}%`} delay={0.2} />
              </div>
            </div>
            <div>
              <SectionLabel delay={0.1}>Architecture Pipeline</SectionLabel>
              <ArchitecturePipeline layers={model.architecture} />
            </div>
            <div>
              <SectionLabel delay={0.14}>Input Features</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {project.features.map((feature) => (
                  <Tag key={`${feature.name}-${feature.source}`} color={T.textSecondary} bg={T.surfaceRaised}>
                    {`${feature.name}: ${feature.source}`}
                  </Tag>
                ))}
              </div>
            </div>
            <MethodologyNote />
          </div>
        )}

        {tab === "Training" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
            <div>
              <SectionLabel>Model</SectionLabel>
              <ModelSwitcher models={project.models} active={activeModel} onChange={setActiveModel} />
            </div>
            <div>
              <SectionLabel delay={0.05}>Loss Curve</SectionLabel>
              <LossChart history={model.trainingHistory} />
            </div>
            <div>
              <SectionLabel delay={0.1}>Training Configuration</SectionLabel>
              <HyperparameterChips params={model.hyperparameters} />
            </div>
          </div>
        )}

        {tab === "Evaluation" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
            <div>
              <SectionLabel>Model</SectionLabel>
              <ModelSwitcher models={project.models} active={activeModel} onChange={setActiveModel} />
            </div>
            <div>
              <SectionLabel delay={0.05}>Cross-Model Comparison</SectionLabel>
              <MetricsComparison models={project.models} />
            </div>
            <div>
              <SectionLabel delay={0.1}>Feature Relevance</SectionLabel>
              <FeatureImportance />
            </div>
          </div>
        )}

        {tab === "Reproduce" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
            <div>
              <SectionLabel>Reproduce It</SectionLabel>
              <ReproduceIt notebookUrl={project.notebookUrl} reportUrl={project.reportUrl} datasetUrl={project.datasetUrl} />
            </div>
            <div>
              <SectionLabel delay={0.08}>Dataset Context</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <MetricCard label="Target" value={project.targetStation} color={T.text} delay={0.1} />
                <MetricCard label="Period" value={project.dateRange} color={T.cyan} delay={0.14} />
                <MetricCard label="Stations" value={project.nearbyStations.length} color={T.green} delay={0.18} />
              </div>
            </div>
            <div>
              <SectionLabel delay={0.12}>Nearby Stations</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {project.nearbyStations.map((station) => <Tag key={station} color={T.textSecondary} bg={T.surfaceRaised}>{station}</Tag>)}
              </div>
            </div>
            <div>
              <SectionLabel delay={0.16}>Stack</SectionLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {project.tags.map((tag) => <Tag key={tag} color={T.textSecondary} bg={T.surfaceRaised}>{tag}</Tag>)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
